//
// Copyright (c) 2023 Emplifi, Inc.
//

import {
  type DowncastConversionApi,
  type Editor,
  type EventInfo,
  type ModelElement,
  Plugin,
  toWidget,
  toWidgetEditable,
  type UpcastConversionApi,
  type UpcastConversionData,
  type ViewDocumentFragment,
  type ViewElement,
  Widget,
} from "ckeditor5";
import InsertAccordionCommand from "./insertAccordionCommand";
import AddAccordionSectionCommand from "./addAccordionSectionCommand";
import RemoveAccordionSectionCommand from "./removeAccordionSectionCommand";
import { ACCORDION, ACCORDION_SECTION_BODY, ACCORDION_SECTION_HEADER } from "./util";

export default class AccordionEditing extends Plugin {
  // noinspection JSUnusedGlobalSymbols
  static get requires() {
    return [Widget];
  }

  // noinspection JSUnusedGlobalSymbols
  static get pluginName() {
    return "AccordionEditing";
  }

  constructor(editor: Editor) {
    super(editor);
    this._setupConfig();
  }

  init() {
    this._setupSchema();
    this._setupConversions();
    this.editor.commands.add("insertAccordion", new InsertAccordionCommand(this.editor));
    this.editor.commands.add("addAccordionSection", new AddAccordionSectionCommand(this.editor));
    this.editor.commands.add(
      "removeAccordionSection",
      new RemoveAccordionSectionCommand(this.editor),
    );
  }

  private _setupConfig() {
    this.editor.config.define("accordion", {
      bodyName: "Section Body",
      bodyPlaceholder: "Section Body",
      headerName: "Section Header",
      headerPlaceholder: "Section Header",
      toolbarName: "Accordion Section Toolbar",
      widgetName: "Accordion",
    });
  }

  private _setupConversions() {
    const conversion = this.editor.conversion;
    // Since there are no "marker" classes on the headers and panels of the accordion, cannot just use a simple
    // upcast definition.  When a wrapper <div class="accordion"> is found, it needs to be converted along with
    // all of its children.
    conversion.for("upcast").add((dispatcher) => {
      dispatcher.on("element:div", this._upcastDivElement.bind(this));
    });
    conversion.for("dataDowncast").elementToElement({
      model: ACCORDION,
      view: {
        name: "div",
        classes: ["accordion"],
      },
    });
    conversion.for("editingDowncast").elementToElement({
      model: ACCORDION,
      view: (_modelItem: ModelElement, { writer }: DowncastConversionApi) => {
        const accordionElement = writer.createContainerElement("div", {
          class: "ak-accordion",
        });
        writer.setCustomProperty(ACCORDION, true, accordionElement);
        return toWidget(accordionElement, writer, {
          label: this.editor.config.get("accordion.widgetName") as string,
        });
      },
    });
    conversion.for("dataDowncast").elementToElement({
      model: ACCORDION_SECTION_HEADER,
      view: "h3",
    });
    conversion.for("editingDowncast").elementToElement({
      model: ACCORDION_SECTION_HEADER,
      view: (_modelItem: ModelElement, { writer }: DowncastConversionApi) => {
        const accordionSectionHeaderElement = writer.createEditableElement("h3", {
          class: "ak-accordion-section-header",
        });
        writer.setCustomProperty(ACCORDION_SECTION_HEADER, true, accordionSectionHeaderElement);
        return toWidgetEditable(accordionSectionHeaderElement, writer, {
          label: this.editor.config.get("accordion.headerName") as string,
        });
      },
    });
    conversion.for("dataDowncast").elementToElement({
      model: ACCORDION_SECTION_BODY,
      view: "div",
    });
    conversion.for("editingDowncast").elementToElement({
      model: ACCORDION_SECTION_BODY,
      view: (_modelItem: ModelElement, { writer }: DowncastConversionApi) => {
        const accordionSectionBodyElement = writer.createEditableElement("div", {
          class: "ak-accordion-section-body",
        });
        writer.setCustomProperty(ACCORDION_SECTION_BODY, true, accordionSectionBodyElement);
        return toWidgetEditable(accordionSectionBodyElement, writer, {
          label: this.editor.config.get("accordion.bodyName") as string,
        });
      },
    });
  }

  private _setupSchema() {
    const schema = this.editor.model.schema;
    schema.register(ACCORDION, {
      inheritAllFrom: "$blockObject",
    });
    schema.register(ACCORDION_SECTION_HEADER, {
      isLimit: true,
      isSelectable: true,
      allowIn: ACCORDION,
      allowContentOf: "$block",
    });
    schema.register(ACCORDION_SECTION_BODY, {
      isLimit: true,
      isSelectable: true,
      allowIn: ACCORDION,
      allowContentOf: "$root",
    });
  }

  private _upcastDivElement(
    _event: EventInfo,
    data: UpcastConversionData<ViewDocumentFragment>,
    conversionApi: UpcastConversionApi,
  ) {
    const { viewItem } = data;
    const accordionConsumable = {
      classes: "accordion",
      name: true,
    };
    if (!conversionApi.consumable.test(viewItem, accordionConsumable)) {
      return;
    }
    const accordionModel = conversionApi.writer.createElement(ACCORDION);
    if (!conversionApi.safeInsert(accordionModel, data.modelCursor)) {
      return;
    }
    conversionApi.consumable.consume(viewItem, accordionConsumable);
    const accordionHeaderConsumable = {
      name: true,
    };
    const accordionBodyConsumable = {
      name: true,
    };
    const panelCount = viewItem.childCount / 2;
    if (viewItem.childCount > 1) {
      for (let panelIndex = 0; panelIndex < panelCount; ++panelIndex) {
        const accordionHeaderViewItem = viewItem.getChild(panelIndex * 2) as ViewElement;
        const accordionBodyViewItem = viewItem.getChild(panelIndex * 2 + 1) as ViewElement | null;
        // NOTE: The jQuery UI Accordion plug-in does not currently care what tags are
        // used, it just alternates between header and body.  If that changes, then
        // we will need to match specific types of elements here.
        // Also, it appears there are some Accordion elements already in responses that are not
        // properly formed, they are missing one of the child elements.  If that happens, then
        // the accordion body item will be null, so make sure to guard against that.
        conversionApi.consumable.consume(accordionHeaderViewItem, accordionHeaderConsumable);
        if (accordionBodyViewItem) {
          conversionApi.consumable.consume(accordionBodyViewItem, accordionBodyConsumable);
        }
        const accordionSectionHeaderModel =
          conversionApi.writer.createElement(ACCORDION_SECTION_HEADER);
        const accordionSectionBodyModel =
          conversionApi.writer.createElement(ACCORDION_SECTION_BODY);
        conversionApi.convertChildren(accordionHeaderViewItem, accordionSectionHeaderModel);
        if (accordionBodyViewItem) {
          conversionApi.convertChildren(accordionBodyViewItem, accordionSectionBodyModel);
        }
        conversionApi.writer.append(accordionSectionHeaderModel, accordionModel);
        conversionApi.writer.append(accordionSectionBodyModel, accordionModel);
      }
    }
    conversionApi.updateConversionResult(accordionModel, data);
  }
}
